import * as React from "react";
import { Utils } from "@/common/utils";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import { Dashboard } from "@/hooks/dashboard";
import { EAIClient } from "Services/EAIClient";
import { useSchoolSettings } from "@/hooks/schools";
import { useAppContext } from "@/contexts/app";
import { DashboardQueries } from "@/common/dashboards";
import { Dropdown } from "react-bootstrap";
import AlertCircleIcon from "Components/Icons/AlertCircleIcon";
import PencilSimpleIcon from "Components/Icons/PencilSimpleIcon";
import ThrashIcon from "Components/Icons/ThrashIcon";
import { MoreVertical } from "lucide-react";

interface DashboardCardProps {
    dashboard: Dashboard
    customize?: boolean
    id?: string
    onClickFavorite?: () => void
    onClickDelete?: (dashboardName: string, dashboardCategory?: string) => void
    onClickEdit?: (dashboard: Dashboard) => void
    isFavorite: boolean
    viewMode?: "grid" | "list"
}

export function DashboardCard(props: DashboardCardProps) {
    const id = props.id || `card-${props.dashboard.short_name.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`;
    const cardTitleId = `dashboard-title-${props.dashboard.id}`;
    const cardFooterId = `dashboard-footer-info-${props.dashboard.id}`;

    const description = <div dangerouslySetInnerHTML={{ __html: props.dashboard.description }} />;

    const { showConfirmModal, dispatchError } = useAppContext();
    const { data: schoolSettings } = useSchoolSettings();
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const navigate = useNavigate();

    const client = new EAIClient();

    const toggleFavorite = async (event: any) => {
        event.preventDefault();
        props.onClickFavorite && props.onClickFavorite();
    };

    const getImageBoxClasses = () => {
        if (props.dashboard.dashboard_type === "BYODashboard") {
            return "image-box byod";
        }
        return "image-box";
    };

    const getDashboardUrl = (dashboardName: string) => {
        let queryString = "";
        if (DashboardQueries[dashboardName] && schoolSettings) {
            queryString = DashboardQueries[dashboardName]?.replace(/60/g, schoolSettings.at_risk_threshold);
        }

        return `${Utils.getLinkBase()}/dashboard/${Utils.getUrlFriendlyString(dashboardName)}${queryString}`;
    };

    const handleCardClick = () => {
        navigate(getDashboardUrl(props.dashboard.name));
    };

    const handleDeleteClick = (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        showConfirmModal({
            element: (
                <span>{`Are you sure you want to delete ${props.dashboard.name}?`}</span>
            ),
            accept: async () => {
                try {
                    await client.deleteBYOCDashboard(props.dashboard.id);
                    if (props.onClickDelete) {
                        props.onClickDelete(props.dashboard.name, props.dashboard.category);
                    }
                } catch (e) {
                    dispatchError(e);
                }
            },
            cancel: () => { },
            showActions: true,
        });
    };

    const handleEditClick = (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (props.onClickEdit) {
            props.onClickEdit(props.dashboard);
        }
    };

    const getUpdatedData = () => {
        if (props.dashboard.datasource == undefined ||
            (props.dashboard.datasource.updated == undefined && props.dashboard.datasource.time_span == undefined)) {
            return "";
        }
        if (props.dashboard.datasource.time_span == undefined) {
            return "Data Updated " + Utils.getValidDateFromString(props.dashboard.datasource.updated, "MM/DD/YY h:mm A");
        } else {
            return "Data from " + props.dashboard.datasource.time_span;
        }
    };

    const updatedDataText = getUpdatedData();

    return (
        <Card
            className={`dashboard-card-container ${props.dashboard.dashboard_type !== "enflux_dashboard" ? "resource" : ""} ${props.viewMode === "list" ? "dashboard-card-list-view" : "dashboard-card-grid-view"}`}
            id={id}
            style={props.viewMode === "grid" ? { minWidth: 332, maxWidth: 332, margin: 0 } : {}}
            role="button"
            aria-labelledby={cardTitleId}
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCardClick();
                }
            }}
        >
            <Card.Body className={`dashboard-card-body ${props.viewMode === "list" ? "dashboard-card-body-list" : "dashboard-card-body-grid"}`}>
                {props.viewMode === "list" ? null : (
                    <div className={getImageBoxClasses()}>
                        <NavLink
                            to={getDashboardUrl(props.dashboard.name)}
                            aria-describedby={cardFooterId}
                            className="dashboard-card-image-link"
                        >
                            <Card.Img
                                data-testid="dashboard-card-image"
                                className="dashboard-card-image"
                                variant="bottom"
                                src={props.dashboard.thumbnail_url_300}
                                alt=""
                            />
                        </NavLink>
                        <div className="dashboard-card-icons"
                            onClick={e => { e.stopPropagation();}}>
                            <div
                                className="icon-star-favorite-container"
                                onClick={toggleFavorite}
                                role="button"
                                tabIndex={0}
                                aria-pressed={props.isFavorite}
                                aria-label={props.isFavorite ? "Dashboard added to favorites" : "Add dashboard to Favorite"}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleFavorite(e);
                                    }
                                }}
                            >
                                <span className="icon-star-favorite">
                                    <i
                                        className={`type icon icon-star${props.isFavorite ? " favorite" : ""}`}
                                        aria-hidden="true"
                                    ></i>
                                    <span className="visually-hidden">
                                        {props.isFavorite ? "Dashboard added to favorites" : "Add dashboard to Favorite"}
                                    </span>
                                </span>
                            </div>
                            <div className="vertical-divider" aria-hidden="true" />
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id={`tooltip-${props.dashboard.id}`} className="dashboard-card-tooltip">
                                        {description}
                                    </Tooltip>
                                }
                            >
                                <div
                                    className="tooltip-icon"
                                    style={{ display: "inline-block", cursor: "pointer" }}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Show description for ${props.dashboard.name}`}
                                >
                                    <AlertCircleIcon aria-hidden="true" />
                                    <span className="visually-hidden">Show description for {props.dashboard.name}</span>
                                </div>
                            </OverlayTrigger>
                        </div>
                    </div>
                )}
            </Card.Body>

            {props.viewMode !== "list" && (
                <Card.Footer className="dashboard-card-footer">
                    <Card.Title className="dashboard-card-title-container">
                        <NavLink to={getDashboardUrl(props.dashboard.name)} aria-describedby={cardFooterId} className="dashboard-card-title-link">
                            <span className={`dashboard-card-title${props.dashboard.dashboard_type !== "enflux_dashboard" ? " resource" : ""}`} id={cardTitleId}>
                                {props.dashboard.name}
                            </span>
                        </NavLink>
                        {props.dashboard.dashboard_type === "BYODashboard" && Utils.hasAdminAccess() && (
                            <Dropdown
                                align="end"
                                className="dashboard-card-dropdown"
                                show={dropdownOpen}
                                onToggle={(nextShow, meta) => {
                                    if (meta.source === "select") return;
                                    setDropdownOpen(nextShow);
                                }}
                                onClick={e => e.stopPropagation()}
                                style={{ cursor: "pointer", marginLeft: 8 }}
                            >
                                <Dropdown.Toggle
                                    as="button"
                                    className="dropdown-toggle-no-style"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setDropdownOpen(!dropdownOpen);
                                    }}
                                    style={{ border: "none", background: "none", padding: 0 }}
                                    aria-haspopup="true"
                                    aria-expanded={dropdownOpen}
                                    aria-controls={`dashboard-dropdown-menu-${props.dashboard.id}`}
                                    aria-label="open dashboard edit menu"
                                >
                                    <MoreVertical size={18} aria-hidden="true" />
                                    <span className="visually-hidden">open dashboard edit menu</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu id={`dashboard-dropdown-menu-${props.dashboard.id}`}>
                                    <Dropdown.Item onClick={handleEditClick} aria-label="edit dashboard name">
                                        <span style={{ marginRight: 8 }}><PencilSimpleIcon aria-hidden="true" /></span> Edit
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={handleDeleteClick} aria-label="delete dashboard">
                                        <span style={{ marginRight: 8 }}><ThrashIcon aria-hidden="true" /></span> Delete
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </Card.Title>

                    <div id={cardFooterId} className="dashboard-card-footer-info">
                        {updatedDataText && (
                            <div className="text-muted dashboard-card-updated">
                                {updatedDataText}
                            </div>
                        )}
                        {props.dashboard.dashboard_type === "BYODashboard" && (
                            <div>
                                <small className="text-muted">BYOContent</small>
                            </div>
                        )}
                    </div>
                </Card.Footer>
            )}
        </Card>
    );
}